import Check from './check';
import X from './x';
import { Fragment } from 'react';
import { pointerCoord } from './util';
import classNames from 'classnames';
import styles from './styles.module.scss';

type Props = {
  checked?: boolean;
  disabled?: boolean;
  defaultChecked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  name?: string;
  value?: string;
  id?: string;
  ariaLabelledby?: string;
  ariaLabel?: string;
  theme?: 'blue' | 'green';
  showIcons?: boolean;
  icons?:
    | boolean
    | {
        checked?: React.ReactNode;
        unchecked?: React.ReactNode;
      };
  trackClassName?: string;
  trackUncheckedClassName?: string;
  thumbClassName?: string;
  tabIndex?: number;
};

type State = {
  checked: boolean;
  hasFocus: boolean;
  disabled?: boolean;
};

export default class ToggleComp extends React.Component<Props, State> {
  activated?: boolean;
  moved?: boolean;
  startX?: number | null;
  previouslyChecked: boolean;
  input: null | HTMLInputElement;

  static displayName = 'ToggleComp';
  static defaultProps = {
    theme: 'blue',
    showIcons: true,
    icons: {
      checked: <Check />,
      unchecked: <X />,
    },
  };

  constructor(props) {
    super(props);
    this.previouslyChecked = !!(props.checked || props.defaultChecked);
    this.input = null;
    this.state = {
      checked: !!(props.checked || props.defaultChecked),
      hasFocus: false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ('checked' in nextProps) {
      this.setState({ checked: !!nextProps.checked });
    }
  }

  handleFocus = () => {
    this.setState({ hasFocus: true });
  };

  handleBlur = () => {
    this.setState({ hasFocus: false });
  };

  handleClick = event => {
    const checkbox = this.input;
    if (event.target !== checkbox && !this.moved) {
      this.previouslyChecked = checkbox?.checked || false;
      event.preventDefault();
      checkbox?.focus();
      checkbox?.click();
      return;
    }

    this.setState({ checked: checkbox?.checked || false });
  };

  handleTouchStart = event => {
    this.startX = pointerCoord(event).x;
    this.activated = true;
  };

  handleTouchMove = event => {
    if (!this.activated) return;
    this.moved = true;

    if (this.startX) {
      const currentX = pointerCoord(event).x;
      if (this.state.checked && currentX + 15 < this.startX) {
        this.setState({ checked: false });
        this.startX = currentX;
        this.activated = true;
      } else if (currentX - 15 > this.startX) {
        this.setState({ checked: true });
        this.startX = currentX;
        this.activated = currentX < (this.startX || 0) + 5;
      }
    }
  };

  handleTouchEnd = event => {
    if (!this.moved) return;
    const checkbox = this.input;
    event.preventDefault();

    if (this.startX) {
      const endX = pointerCoord(event).x;
      if (this.previouslyChecked === true && this.startX + 4 > endX) {
        if (this.previouslyChecked !== this.state.checked) {
          this.setState({ checked: false });
          this.previouslyChecked = this.state.checked;
          checkbox?.click();
        }
      } else if (this.startX - 4 < endX) {
        if (this.previouslyChecked !== this.state.checked) {
          this.setState({ checked: true });
          this.previouslyChecked = this.state.checked;
          checkbox?.click();
        }
      }

      this.activated = false;
      this.startX = null;
      this.moved = false;
    }
  };

  getIcon = type => {
    const { icons } = this.props;
    if (!icons) {
      return null;
    }
    return icons[type] === undefined
      ? ToggleComp.defaultProps.icons[type]
      : icons[type];
  };

  render() {
    const {
      showIcons,
      theme,
      trackUncheckedClassName,
      trackClassName,
      thumbClassName,
      tabIndex,
      ...inputProps
    } = this.props;

    return (
      <div
        className={classNames(
          styles['react-toggle'],
          this.state.checked && styles['react-toggle--checked'],
          this.state.hasFocus && styles['react-toggle--focus'],
          this.state.disabled && styles['react-toggle--disabled'],
        )}
        onClick={this.handleClick}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEnd}
      >
        <div
          className={classNames(
            styles['react-toggle-track'],
            styles[`react-toggle-track--${theme}`],
            trackClassName && styles[trackClassName],
            !this.state.checked &&
              trackUncheckedClassName &&
              styles[trackUncheckedClassName],
          )}
        >
          {showIcons && (
            <Fragment>
              <div className={styles['react-toggle-track-check']}>
                {this.getIcon('checked')}
              </div>
              <div className={styles['react-toggle-track-x']}>
                {this.getIcon('unchecked')}
              </div>
            </Fragment>
          )}
        </div>
        <div
          className={classNames(
            styles['react-toggle-thumb'],
            styles[`react-toggle-thumb--${theme}`],
            thumbClassName && styles[thumbClassName],
          )}
        />

        <input
          {...inputProps}
          ref={ref => {
            this.input = ref;
          }}
          tabIndex={tabIndex ?? 0}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          className={styles['react-toggle-screenreader-only']}
          type='checkbox'
        />
      </div>
    );
  }
}
